import { Helmet } from "react-helmet-async";
import { AddToCalendarButton } from "./AddToCalendarButton";
import { HavingTroubleLink } from "./HavingTroubleLink";
import {
  formatLivestreamCountdown,
  formatLivestreamStartSchedule,
  type LivestreamSchedulePhase,
} from "./livestreamSchedule";
import { useLivestreamScheduleClock } from "./useLivestreamSchedulePhase";

function getWaitingMessage({
  phase,
  start,
  now,
}: {
  phase: LivestreamSchedulePhase;
  start?: string | null;
  now: Date;
}): string {
  switch (phase) {
    case "before-start-far":
      return "";
    case "before-start-countdown":
      return start
        ? formatLivestreamCountdown({ target: start, now })
        : "00:00";
    case "waiting-for-host":
      return "Starts soon";
    case "ended":
      return "Livestream has ended";
    default:
      return "";
  }
}

export function LivestreamWaitingScreen({
  productName,
  imageUrl,
  pageTitle,
  start,
  end,
  meetingUrl,
  phase: phaseOverride,
  onOpenChat,
}: {
  productName: string;
  imageUrl?: string;
  pageTitle: string;
  start?: string | null;
  end?: string | null;
  meetingUrl?: string;
  phase?: LivestreamSchedulePhase;
  onOpenChat?: () => void;
}) {
  const { now, phase: computedPhase } = useLivestreamScheduleClock({ start, end });
  const phase = phaseOverride ?? computedPhase;

  const showImage =
    phase === "before-start-far" ||
    phase === "before-start-countdown" ||
    phase === "waiting-for-host";
  const message = getWaitingMessage({ phase, start, now });
  const isCountdown = phase === "before-start-countdown";
  const showSchedule =
    start && (phase === "before-start-far" || phase === "before-start-countdown");
  const schedule = showSchedule ? formatLivestreamStartSchedule({ start }) : null;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="min-h-dvh flex flex-col bg-black text-white">
        <div className="flex flex-1 flex-col items-center justify-center px-6">
          <div className="w-full max-w-sm space-y-6 text-center">
            {showImage && imageUrl ? (
              <img
                src={imageUrl}
                alt={productName}
                className="mx-auto h-40 w-40 rounded-lg object-cover shadow-xl"
              />
            ) : null}
            <div>
              <h1 className="text-2xl font-bold">{productName}</h1>
              {message ? (
                <p
                  className={`mt-3 text-white/80 ${isCountdown ? "font-mono text-3xl tracking-wider" : "text-lg"}`}
                >
                  {message}
                </p>
              ) : null}
              {schedule ? (
                <div className={`space-y-3 text-white/50 ${message ? "mt-2" : "mt-3"}`}>
                  <div className="space-y-1">
                    <p className="text-2xl">{schedule.date}</p>
                    <p className="text-sm">{schedule.time}</p>
                  </div>
                  <AddToCalendarButton
                    title={productName}
                    start={start}
                    end={end}
                    url={meetingUrl}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {onOpenChat ? (
          <div className="shrink-0 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <HavingTroubleLink onOpenChat={onOpenChat} />
          </div>
        ) : null}
      </div>
    </>
  );
}
